import React, { useState } from 'react';
import { Button } from 'app/components/ui/button';
import { Input } from 'app/components/ui/input';
import { Label } from 'app/components/ui/label';
import { Textarea } from 'app/components/ui/textarea';
import { Separator } from 'app/components/ui/separator';
import { Alert, AlertDescription } from 'app/components/ui/alert';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from 'app/components/ui/dialog';
import {
  Send,
  Send as SendIcon,
  CheckCircle,
  AlertCircle,
  Loader2,
  TestTube,
} from 'lucide-react';
import { useToast } from 'app/components/ui/use-toast';

interface TestEmailData {
  testEmail: string;
  subject: string;
  message: string;
}

interface MailgunTestPopupProps {
  isOpen: boolean;
  onClose: () => void;
  onSendTest: (testData: TestEmailData) => Promise<any>;
}

const MailgunTestPopup: React.FC<MailgunTestPopupProps> = ({
  isOpen,
  onClose,
  onSendTest,
}) => {
  const { toast } = useToast();
  const [loading, setLoading] = useState(false);
  const [testResult, setTestResult] = useState<any>(null);

  const [testData, setTestData] = useState<TestEmailData>({
    testEmail: '',
    subject: 'Test Email from Mailgun Configuration',
    message:
      'This is a test email to verify your Mailgun configuration is working correctly.',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);
    setTestResult(null);

    try {
      if (!testData.testEmail) {
        throw new Error('Please enter a test email address');
      }

      const result = await onSendTest(testData);
      setTestResult({
        success: true,
        message: 'Test email sent successfully!',
        details: result,
      });

      toast({
        title: 'Test Email Sent',
        description: 'Test email sent successfully via Mailgun',
      });
    } catch (error) {
      console.error('Error sending test email:', error);
      setTestResult({
        success: false,
        message:
          error instanceof Error ? error.message : 'Failed to send test email',
        details: error,
      });

      toast({
        title: 'Test Failed',
        description:
          error instanceof Error ? error.message : 'Failed to send test email',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setTestData({
      testEmail: '',
      subject: 'Test Email from Mailgun Configuration',
      message:
        'This is a test email to verify your Mailgun configuration is working correctly.',
    });
    setTestResult(null);
    onClose();
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <div className="p-2 rounded-lg bg-purple-500 text-white">
              <Send className="h-5 w-5" />
            </div>
            <div>
              <div className="text-lg font-bold">Test Mailgun Email</div>
              <DialogDescription>
                Send a test email via Mailgun
              </DialogDescription>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="testEmail">Test Email Address *</Label>
            <Input
              id="testEmail"
              type="email"
              placeholder="test@example.com"
              value={testData.testEmail}
              onChange={e =>
                setTestData({ ...testData, testEmail: e.target.value })
              }
              required
            />
            <p className="text-xs text-muted-foreground">
              Enter the email address where you want to send the test email
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="subject">Subject</Label>
            <Input
              id="subject"
              placeholder="Test Email Subject"
              value={testData.subject}
              onChange={e =>
                setTestData({ ...testData, subject: e.target.value })
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="message">Message</Label>
            <Textarea
              id="message"
              placeholder="Test email message..."
              value={testData.message}
              onChange={e =>
                setTestData({ ...testData, message: e.target.value })
              }
              rows={3}
            />
          </div>

          <Separator />

          {/* Test Result */}
          {testResult && (
            <Alert variant={testResult.success ? 'default' : 'destructive'}>
              {testResult.success ? (
                <CheckCircle className="h-4 w-4" />
              ) : (
                <AlertCircle className="h-4 w-4" />
              )}
              <AlertDescription>
                <strong>{testResult.success ? 'Success!' : 'Error'}</strong>
                <br />
                {testResult.message}
              </AlertDescription>
            </Alert>
          )}

          {/* Action Buttons */}
          <div className="flex items-center justify-end gap-2 pt-4">
            <Button type="button" variant="outline" onClick={handleClose}>
              Close
            </Button>
            <Button type="submit" disabled={loading || !testData.testEmail}>
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <SendIcon className="h-4 w-4 mr-2" />
                  Send Test Email
                </>
              )}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default MailgunTestPopup;
